import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useLazyProfileQuery,
  useUpdateMobileNumberMutation,
} from "../../../../../redux/api/user/userAPI";
import { selectAuth } from "../../../../../redux/features/auth/auth-slice";
import { OvstErrorCode } from "../../../enums/ovst-error-codes";
import { AppRoute } from "../../../interfaces/app-routes";
import AppButton from "../../../ui-elements/buttons/app-button/app-button";
import AppInput from "../../../ui-elements/input/app-input";
import SpinnerModal from "../../../ui-elements/spinner/spinner";
import { mobileNumberRegex } from "../../../utils/regex";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toast";

export interface IChangeMobileNumberFormInput {
  phoneNumber: string;
}

const defaultFormValues: IChangeMobileNumberFormInput = {
  phoneNumber: "",
};

const ChangeMobileNumberForm = () => {
  const navigate = useNavigate();
  const [isError, setIsError] = useState(false);
  const auth = useSelector(selectAuth);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IChangeMobileNumberFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const [updateMobileNumber, { isLoading: isLoadingUpdateMobileNumber }] =
    useUpdateMobileNumberMutation();
  const [getProfile, { isFetching: isGetProfileFetching }] =
    useLazyProfileQuery();

  const onSubmit = (data: IChangeMobileNumberFormInput) => {
    updateMobileNumber({ contactNumber: data.phoneNumber })
      .unwrap()
      .then(() => {
        return getProfile({ role: auth.userRole }).unwrap();
      })
      .then(() => {
        navigate(AppRoute.USER_PROFILE);
        showSuccessMessage("Mobile number updated successfully");
        reset({ ...defaultFormValues });
      })
      .catch((error) => {
        if (
          error.status === 412 &&
          error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
        ) {
          setIsError(true);
        } else {
          showErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (errors.phoneNumber) {
      setIsError(false);
    }
  }, [errors.phoneNumber]);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col>
            <AppInput
              name="phoneNumber"
              placeholder="New Mobile Number"
              label="New Mobile Number"
              type="number"
              register={register("phoneNumber", {
                required: "Mobile number is required",
                pattern: {
                  value: mobileNumberRegex,
                  message: "Please enter a valid mobile number",
                },
              })}
              onKeyDown={(event) => {
                ["e", "E", "+", "-", "."].includes(event.key) &&
                  event.preventDefault();
              }}
              errors={errors}
            />
          </Col>
        </Row>
        <Row>
          {isError && (
            <Col className="text-danger justify-content-center font-size-14 mt-2 text-center">
              Mobile number is already associated with an account
            </Col>
          )}
        </Row>
        <Row className="mt-5">
          <Col>
            <AppButton
              text={`Confirm`}
              size="medium"
              type="submit"
              isLoading={isLoadingUpdateMobileNumber}
            />
          </Col>
        </Row>
      </form>
      <SpinnerModal show={isGetProfileFetching} />
    </div>
  );
};

export default ChangeMobileNumberForm;
